var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { FormModel } from "./FormModel";
import { OrganizationUpdateFailureReason } from "healthex-common/organization.dtos";
export class AdminLoginSettingsViewModel {
    constructor(opts) {
        this.formModel = new FormModel({
            turnOnSSO: false,
            domain: "",
            ...this.getDefaultSSOConfigurationInput(),
        }, ["ssoProviderName", "idpMetadataUrl", "domain", "emailAttributeName", "firstNameAttributeName", "lastNameAttributeName"]);
        this.organization = NOT_LOADED;
        this.organizationsManager = opts.container.organizationsManager;
        this.onSave = opts.onSave;
        void this.loadOrganization();
    }
    get turnOnSSOSelected() {
        if (this.organization === NOT_LOADED)
            return false;
        return this.formModel.input.turnOnSSO;
    }
    get showSSORequirement() {
        var _a;
        if (this.organization === NOT_LOADED)
            return false;
        return !((_a = this.organization) === null || _a === void 0 ? void 0 : _a.ssoEnabled);
    }
    async updateTurnOnSSO(input) {
        this.formModel.updateInput({ turnOnSSO: input });
    }
    validateInput() {
        this.formModel.clearErrors();
        const { notBlank, validURL } = validators;
        const f = this.formModel;
        if (f.input.turnOnSSO) {
            f.validateField("ssoProviderName", notBlank, "Enter your SSO provider name");
            f.validateField("idpMetadataUrl", notBlank, "Enter your metadata URL");
            f.validateField("domain", notBlank, "Enter a valid email domain name");
            f.validateField("emailAttributeName", notBlank, "Enter an email IDP attribute");
            f.validateField("firstNameAttributeName", notBlank, "Enter a first name IDP attribute");
            f.validateField("lastNameAttributeName", notBlank, "Enter a last name IDP attribute");
            f.validateField("idpMetadataUrl", validURL, "Enter a valid metadata URL");
        }
        return !f.hasErrors();
    }
    validateResponse(response) {
        if (!response)
            return true;
        const f = this.formModel;
        const startErrorCount = f.errorCount();
        if (response === OrganizationUpdateFailureReason.PUBLIC_EMAIL_DOMAIN_CANNOT_BE_USED) {
            f.addError("domain", "Enter a non public email domain");
        }
        else if (response === OrganizationUpdateFailureReason.DOMAIN_MUST_BE_THE_SAME_AS_THE_USER_EMAIL_DOMAIN) {
            f.addError("domain", "Enter your organization's email domain");
        }
        else if (response === OrganizationUpdateFailureReason.DOMAIN_ALREADY_USED) {
            f.addError("domain", "Enter a domain that hasn't already been taken");
        }
        return f.errorCount() === startErrorCount;
    }
    async save() {
        if (this.organization === NOT_LOADED || !this.organization)
            return;
        if (!this.validateInput())
            return;
        const { turnOnSSO, ssoProviderName, idpMetadataUrl, domain, emailAttributeName, firstNameAttributeName, lastNameAttributeName, titleAttributeName, permissionsAttributeName, phoneAttributeName, } = this.formModel.input;
        const updatedOrganizationDetailsInput = {
            type: this.organization.type,
            domain: turnOnSSO ? domain : "",
            ssoEnabled: turnOnSSO || false,
            ssoConfiguration: turnOnSSO
                ? {
                    ssoProviderName,
                    idpMetadataUrl,
                    idpAttributes: {
                        email: emailAttributeName,
                        firstName: firstNameAttributeName,
                        lastName: lastNameAttributeName,
                        title: titleAttributeName.length ? titleAttributeName : undefined,
                        permissions: permissionsAttributeName.length ? permissionsAttributeName : undefined,
                        phone: phoneAttributeName.length ? phoneAttributeName : undefined,
                    },
                }
                : undefined,
        };
        const response = await this.organizationsManager.updateOrganizationDetails(this.organization.id, updatedOrganizationDetailsInput);
        if (!this.validateResponse(response))
            return;
        await this.loadOrganization();
        this.onSave();
    }
    getDefaultSSOConfigurationInput() {
        return {
            ssoProviderName: "",
            idpMetadataUrl: "",
            emailAttributeName: "",
            firstNameAttributeName: "",
            lastNameAttributeName: "",
            titleAttributeName: "",
            permissionsAttributeName: "",
            phoneAttributeName: "",
        };
    }
    async loadOrganization() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        const organization = this.organizationsManager.myOrganization.value;
        if (organization && organization !== NOT_LOADED) {
            this.organization = organization;
            this.formModel.updateInput({
                turnOnSSO: organization.ssoEnabled,
                domain: organization.domain,
                ssoProviderName: (_b = (_a = organization.ssoConfiguration) === null || _a === void 0 ? void 0 : _a.ssoProviderName) !== null && _b !== void 0 ? _b : "",
                idpMetadataUrl: (_d = (_c = organization.ssoConfiguration) === null || _c === void 0 ? void 0 : _c.idpMetadataUrl) !== null && _d !== void 0 ? _d : "",
                emailAttributeName: (_f = (_e = organization.ssoConfiguration) === null || _e === void 0 ? void 0 : _e.idpAttributes.email) !== null && _f !== void 0 ? _f : "",
                firstNameAttributeName: (_h = (_g = organization.ssoConfiguration) === null || _g === void 0 ? void 0 : _g.idpAttributes.firstName) !== null && _h !== void 0 ? _h : "",
                lastNameAttributeName: (_k = (_j = organization.ssoConfiguration) === null || _j === void 0 ? void 0 : _j.idpAttributes.lastName) !== null && _k !== void 0 ? _k : "",
                titleAttributeName: (_m = (_l = organization.ssoConfiguration) === null || _l === void 0 ? void 0 : _l.idpAttributes.title) !== null && _m !== void 0 ? _m : "",
                permissionsAttributeName: (_p = (_o = organization.ssoConfiguration) === null || _o === void 0 ? void 0 : _o.idpAttributes.permissions) !== null && _p !== void 0 ? _p : "",
                phoneAttributeName: (_r = (_q = organization.ssoConfiguration) === null || _q === void 0 ? void 0 : _q.idpAttributes.phone) !== null && _r !== void 0 ? _r : "",
            });
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminLoginSettingsViewModel.prototype, "organization", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminLoginSettingsViewModel.prototype, "turnOnSSOSelected", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminLoginSettingsViewModel.prototype, "showSSORequirement", null);
